<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";

import StickyTypeItemTooltip from "@/components/search/tooltips/StickyTypeItemTooltip.vue";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import InfoMarker from "@/components/ui/InfoMarker/InfoMarker.vue";
import SelectableList from "@/components/ui/SelectableList/SelectableList.vue";
import TypeDot from "@/components/ui/TypeDot/TypeDot.vue";
import type { Color } from "@/model/baseTypes";
import { useBoardStore } from "@/store/board";
import { isStickyTypeAlmSynced } from "@/store/stickyType";

interface TypeItem {
  id: string;
  name: string;
  color: Color;
}

const props = defineProps<{ selected: readonly string[] }>();
const emit = defineEmits<{ change: [typeIds: string[]] }>();

const items = computed(() =>
  useBoardStore().possibleStickyTypes.flatMap((type) =>
    type.origin === "team" && isStickyTypeAlmSynced(type)
      ? ([{ id: type.id, name: type.name, color: type.color }] as TypeItem[])
      : [],
  ),
);

const selection = computed(() =>
  props.selected.flatMap((id) => items.value.find((item) => item.id === id) || []),
);

// Internal copy allows user to toggle selections before committing changes on close.
// This provides better UX as the dropdown stays open during multi-selection.
const internalSelected = ref<TypeItem[]>([]);

// Sync internal state when props change (e.g., when filters are reset externally)
watchEffect(() => {
  const selectedIds = new Set(props.selected);
  internalSelected.value = items.value.filter((item) => selectedIds.has(item.id));
});

/**
 * Only emit if selection changed
 */
const handleClose = () => {
  const prevValues = new Set(props.selected);
  const newValues = new Set(internalSelected.value.map((item) => item.id));

  if (prevValues.symmetricDifference(newValues).size) {
    emit(
      "change",
      internalSelected.value.map((item) => item.id),
    );
  }
};
</script>

<template>
  <DropdownMenu
    class="issue-type-filter"
    disable-teleport
    stay-open-on-content-click
    width="md"
    @close="handleClose"
  >
    <!-- Trigger -->
    <template #trigger="{ isOpen }">
      <IconButton
        icon="general/sticky"
        variant="outlined"
        size="small"
        :activated="isOpen"
        :aria-expanded="isOpen"
        aria-haspopup="listbox"
        :aria-label="$t('label.search.filterButton', { value: $t('general.stickyTypes') })"
      >
        <template #tooltip>
          <StickyTypeItemTooltip :types="selection" />
        </template>
      </IconButton>
      <InfoMarker v-if="selected.length" :top="4" :right="4" />
    </template>

    <SelectableList
      v-model="internalSelected"
      role="dialog"
      :header="$t('general.stickyTypes')"
      :values="items"
    >
      <template #before="{ item }">
        <TypeDot :color="item.color" size="medium" />
      </template>
      <template #default="{ item }">
        {{ item.name }}
      </template>
    </SelectableList>
  </DropdownMenu>
</template>
